import React from 'react';
import { Outlet, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { MobileNav } from './layout/MobileNav';
import { PwaInstallBanner } from './pwa/PwaInstallBanner';
import { useUser } from '../hooks/useUser';
import { TimeDisplay } from './TimeDisplay';
import MetarDisplay from './MetarDisplay';
import { MetarProvider } from '../contexts/MetarContext';
import { HelpCircle } from 'lucide-react';
import { FeedbackButton } from './FeedbackButton';
import { WhatsNewProvider } from './whatsNew/WhatsNewContext';
import { WhatsNewPanel } from './whatsNew/WhatsNewPanel';
import { getRoleDisplayName } from '../types/user';
import { logger } from '../utils/logger';
import { useLanguage } from '../contexts/LanguageContext';

const Layout: React.FC = () => {
  const { logout } = useAuth();
  const { isAdmin, user } = useUser();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/');
    } catch (error) {
      logger.error('Failed to log out:', error);
    }
  };

  const handleHelpClick = () => {
    const subject = encodeURIComponent('OmniFlyer Support & Feedback');
    const body = encodeURIComponent(`Hi OmniFlyer team,

I'm reaching out for support/feedback regarding the OmniFlyer application.

[Please describe your question, feedback, or issue here]

Best regards,
[Your name]`);
    
    window.open(`mailto:hello@omniflyer.com?subject=${subject}&body=${body}`, '_blank');
  };

  return (
    <MetarProvider>
      <WhatsNewProvider>
      <div className="h-screen bg-gray-50 flex flex-col">
        {/* Top Bar - Full Width */}
        <nav className="bg-white shadow-sm w-full">
          <div className="px-4 sm:px-6 lg:px-8">
            <div className="flex justify-between h-16">
              <div className="flex items-center w-full pl-12 lg:pl-0">
                <h1 className="text-xl font-semibold text-gray-800 mr-auto">OmniFlyer</h1>
              </div>
              <div className="flex items-center space-x-1 sm:space-x-4">
                {/* METAR Display — hidden on small screens to keep the bar uncluttered */}
                <div className="hidden lg:block">
                  <MetarDisplay />
                </div>

                {/* Time Display — hidden below sm */}
                <div className="hidden sm:block">
                  <TimeDisplay />
                </div>
                
                {/* Role Label */}
                <div data-testid="role-badge" className={`px-3 py-1 rounded-full text-sm font-medium ${
                  isAdmin 
                    ? 'bg-blue-100 text-blue-800' 
                    : user?.role === 'cfi'
                    ? 'bg-green-100 text-green-800'
                    : user?.role === 'student'
                    ? 'bg-purple-100 text-purple-800'
                    : user?.role === 'dispatch'
                    ? 'bg-orange-100 text-orange-800'
                    : user?.role === 'maintenance'
                    ? 'bg-red-100 text-red-800'
                    : 'bg-gray-100 text-gray-800'
                }`}>
                  {user?.role ? getRoleDisplayName(user.role) : 'Member'}
                </div>
                
                {/* Help Button */}
                <button
                  onClick={handleHelpClick}
                  className="h-8 px-2 sm:px-3 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors flex items-center space-x-1 whitespace-nowrap"
                  title="Get help or send feedback"
                  aria-label="Contact support and send feedback"
                >
                  <HelpCircle className="h-4 w-4" />
                  <span className="hidden sm:inline">{t('navigation.help')}</span>
                </button>
                
                {/* Sign Out Button - One Liner */}
                <button
                  onClick={handleLogout}
                  className="h-8 px-2 sm:px-3 text-sm text-gray-700 hover:text-gray-900 hover:bg-gray-100 rounded transition-colors flex items-center whitespace-nowrap"
                  style={{lineHeight: 1}}
                >
                  {t('navigation.signOut')}
                </button>
              </div>
            </div>
          </div>
        </nav>

        <PwaInstallBanner />

        <div className="flex flex-1 h-full lg:pb-12">
          {/* Nav: fixed sidebar on lg+, hamburger drawer below lg */}
          <MobileNav />
          {/* Main Content */}
          <div className="flex-1 p-4 lg:p-6 overflow-y-auto">
            <Outlet />
          </div>
        </div>

        {/* Fixed Footer */}
        <footer className="hidden lg:block fixed bottom-0 left-0 right-0 bg-gray-800 text-white py-3 px-4 text-center text-sm z-50">
          <div className="flex justify-between items-center max-w-screen-xl mx-auto">
            <span className="text-gray-300">© 2026 OmniFlyer</span>
            <div className="flex space-x-4 text-gray-300">
              <a href="/privacy" className="hover:text-white transition-colors">Privacy</a>
              <a href="/terms" className="hover:text-white transition-colors">Terms</a>
            </div>
          </div>
        </footer>
      </div>
      
      {/* Floating Feedback Button */}
      {/* Desktop only — on mobile, Feedback lives in the nav drawer instead of
          a floating button that covers content. */}
      <FeedbackButton className="hidden lg:block" />

      {/* What's new changelog slide-over — rendered once, opened from the
          Sidebar entry point (OMN-368). */}
      <WhatsNewPanel />
      </WhatsNewProvider>
    </MetarProvider>
  );
};

export default Layout; 